/**
 * ContactDetail (conversation view)
 *
 * The single unified communication window per person, conversation-first: a
 * slim identity header (name, phone/email/lead-since, study chips, call +
 * lifecycle + owner controls), then a fixed-height conversation card —
 * CommsThread as the scrollable chat region with the Composer docked at the
 * bottom — beside the follow-up tasks, screener answers and humanized
 * automation enrollments (+ super-admin test re-enroll). Reached at
 * /crm/contacts/:id. Data via the
 * authed backend API; live via CommsThread's Realtime broadcast + fallback
 * poll and a reloadToken bump on send.
 *
 * ONE responsive DOM at every width: LeadHeader collapses its controls into the
 * ⋯ Actions menu (which becomes a bottom sheet below sm), and LeadRail docks
 * itself as a swipe-up sheet below lg — so the conversation card owns a phone
 * screen without this page rendering a second mobile tree.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ChevronLeft, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ContactDetailResponse, Enrollment, Contact, ScheduleReadiness, ScheduleReadinessItem, contactName } from '../types';
import { getLeadOrigin, leadOriginLabel, notifyStatsChanged, useTeam } from '../lib';
import { CommsThread, Composer } from '../components';
import { AgentThreadState } from '../components/AgentPanel';
import { EscalationAnchor } from '../components/escalationDisplay';
import EditContactModal from '../components/EditContactModal';
import StudyReferenceDrawer from '../components/studies/StudyReferenceDrawer';
import { Alert } from '../components/ui';
import LeadHeader from '../components/lead/LeadHeader';
import AttentionStrip from '../components/lead/AttentionStrip';
import LeadRail from '../components/lead/rail/LeadRail';

/** Human copy for a compliance-gate block (same set the composer surfaces). */
function blockedReason(reason?: string): string {
  switch (reason) {
    case 'opted_out': return 'contact has opted out of SMS';
    case 'consent_revoked': return 'SMS consent was revoked';
    case 'quiet_hours': return 'outside allowed messaging hours';
    case 'no_phone': return 'contact has no phone number';
    case 'no_comprehensive_form': return 'no published comprehensive form — publish one under Forms first';
    default: return reason || 'blocked';
  }
}

export default function ContactDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { byId: usersById } = useTeam();
  const isSuperAdmin = user?.role === 'super_admin';

  const [detail, setDetail] = useState<ContactDetailResponse | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  // Bumps the ScheduledMessagesBar (new schedule + fired-send ping) WITHOUT
  // forcing CommsThread's scroll-to-bottom the way reloadToken would.
  const [schedToken, setSchedToken] = useState(0);
  const [reenrolling, setReenrolling] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [archBusy, setArchBusy] = useState(false);
  const [archError, setArchError] = useState<string | null>(null);
  const [snoozeBusy, setSnoozeBusy] = useState(false);
  const [snoozeError, setSnoozeError] = useState<string | null>(null);
  const [draftingOptions, setDraftingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [refOpen, setRefOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Scheduling readiness for the lead's current study — powers the rail checklist
  // and the header's "requirements not met" warning on the lifecycle changer.
  const [scheduleReadiness, setScheduleReadiness] = useState<ScheduleReadiness | null>(null);
  // One-shot draft pushed from a rail quick action (booking link) into the
  // docked Composer; cleared the moment the Composer consumes it.
  const [composerDraft, setComposerDraft] = useState<{ text: string; mode?: 'sms' } | null>(null);
  // Notes-only view (owner ask 2026-08-07): double-clicking the composer's Note
  // pill consolidates the recruiters' notes on this lead — the thread narrows to
  // channel='note' (server-side, so "Load older" pages notes) and its banner is
  // the way back out. Per-lead: it resets whenever the route's contact changes,
  // so a filtered thread never carries over to the next lead.
  const [notesOnly, setNotesOnly] = useState(false);
  useEffect(() => { setNotesOnly(false); }, [id]);
  // Texting-agent thread state, reported up by AgentPanel's fetch (C-12/C-13):
  // feeds the thread's draft-pending strip and the composer's pause warning
  // without a second probe. null = no open agent conversation.
  const [agentThread, setAgentThread] = useState<AgentThreadState | null>(null);
  // Escalation-banner deep-link target: which concern to jump to, plus a nonce
  // so clicking the same concern twice re-fires the scroll/highlight. Reset on
  // navigation between leads so a stale target never fires on the next lead.
  const [escalationFocus, setEscalationFocus] = useState<{ anchor: EscalationAnchor; nonce: number } | null>(null);
  // Debounced (~1s) Realtime ping for rail cards (RecordsCard): backend state
  // changes already emit the broadcast; bumping reloadToken instead would
  // force CommsThread's scroll-to-bottom on every background ping.
  // Back target: the CRM page this lead was opened from (recorded by
  // CRMRoutes), falling back to the Leads inbox on a deep link / fresh tab.
  const backTo = getLeadOrigin() || '/crm/inbox';
  const backLabel = leadOriginLabel(backTo);
  const [pingTick, setPingTick] = useState(0);
  const pingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPing = useCallback(() => {
    if (pingTimer.current) return;
    pingTimer.current = setTimeout(() => {
      pingTimer.current = null;
      setPingTick((t) => t + 1);
    }, 1000);
  }, []);
  useEffect(() => () => {
    if (pingTimer.current) clearTimeout(pingTimer.current);
  }, []);

  const loadEnrollments = useCallback(async () => {
    try {
      const enr = await api.get(`/api/crm/contacts/${id}/enrollments`);
      setEnrollments(enr.data?.enrollments || []);
    } catch {
      setEnrollments([]);
    }
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res] = await Promise.all([api.get(`/api/crm/contacts/${id}`), loadEnrollments()]);
      setDetail(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [id, loadEnrollments]);

  useEffect(() => { load(); }, [load]);

  // Drop any escalation-banner jump target when moving between leads, so a
  // click on the previous lead never fires a scroll/highlight on this one.
  useEffect(() => { setEscalationFocus(null); }, [id]);

  // Silent contact refetch when the timeline changes (reloadToken bumps after
  // every send): the DB trigger has already moved the worklist columns
  // (attempt_count / needs_reply_at / next_action_at) by the time the send API
  // responds, and CallbacksCard's cadence line reads them from this response.
  // No setLoading — a full-page spinner on every send would be jarring.
  useEffect(() => {
    if (reloadToken === 0) return;
    let stale = false;
    api.get(`/api/crm/contacts/${id}`)
      .then((res) => { if (!stale) setDetail(res.data); })
      .catch(() => { /* next full load or send retries */ });
    return () => { stale = true; };
  }, [reloadToken, id]);

  // Scheduling readiness — refetched when the lead's study changes, on send/ping,
  // and after a stage change (an auto gate like "expresses interest" can flip).
  const primaryStudyId = detail?.primary_study?.id ?? detail?.contact.primary_study_id ?? null;
  const contactLifecycle = detail?.contact.lifecycle_status ?? null;
  const loadReadiness = useCallback(async () => {
    if (!primaryStudyId) { setScheduleReadiness(null); return; }
    try {
      const res = await api.get(`/api/crm/contacts/${id}/schedule-gates`, { params: { study_id: primaryStudyId } });
      setScheduleReadiness(res.data);
    } catch {
      setScheduleReadiness(null);
    }
  }, [id, primaryStudyId]);
  useEffect(() => { loadReadiness(); }, [loadReadiness, reloadToken, pingTick, contactLifecycle]);

  // Tick a manual scheduling gate (CRC / investigator / custom sign-off). The
  // POST returns the recomputed readiness so the header warning updates too.
  const onToggleGate = useCallback(async (item: ScheduleReadinessItem, done: boolean) => {
    const studyId = scheduleReadiness?.study_id;
    if (!studyId) return;
    try {
      const res = await api.post(`/api/crm/contacts/${id}/schedule-gates`, {
        study_id: studyId, gate_key: item.key, done,
      });
      if (res.data?.readiness) setScheduleReadiness(res.data.readiness);
      else await loadReadiness();
    } catch {
      await loadReadiness();
    }
  }, [id, scheduleReadiness?.study_id, loadReadiness]);

  const reEnroll = async () => {
    setReenrolling(true);
    setMsg(null);
    try {
      const res = await api.post(`/api/crm/contacts/${id}/re-enroll`);
      const d = res.data || {};
      const enrolledCount = (d.results || []).filter((r: any) => r.enrolled).length;
      const tick = d.tick || {};
      await loadEnrollments();
      setReloadToken((t) => t + 1);
      setMsg(`Re-enrolled into ${enrolledCount} flow(s); tick claimed ${tick.claimed ?? 0}, SMS enqueued ${tick.enqueuedSms ?? 0}. The welcome sends shortly.`);
    } catch (e: any) {
      const reason = e?.response?.data?.reason;
      setMsg(`Re-enroll failed: ${reason ? `refused (${reason})` : (e?.response?.data?.detail || e?.response?.data?.error || e.message)}`);
    } finally {
      setReenrolling(false);
    }
  };

  /** Draft the three ways forward (book / form / not interested) into the docked
   *  composer — Phase D respond-options quick action. Minting on { send:false }
   *  returns the same rendered text the send path would have used (live booking
   *  + form links), so the recruiter edits the wording and sends it on the normal
   *  gated /messages path; nothing goes out from this button. Mirrors the
   *  booking-link and form-link draft actions on the rail. */
  const draftOptions = async () => {
    if (draftingOptions) return;
    setDraftingOptions(true);
    setOptionsError(null);
    setMsg(null);
    try {
      const res = await api.post(`/api/crm/contacts/${id}/respond-options`, { send: false });
      const body: string | undefined = res.data?.body;
      if (!body) throw new Error('No options message came back');
      setComposerDraft({ text: body, mode: 'sms' });
      setMsg('Options draft added to the message box — edit and send from there. They can reply 1 (book a call), 2 (fill the form) or 3 (not interested).');
    } catch (e: any) {
      const d = e?.response?.data || {};
      if (e?.response?.status === 409 && d.reason) {
        setOptionsError(`Options message not drafted: ${blockedReason(d.reason)}.`);
      } else {
        const base = d.detail || d.error || e.message || 'Draft failed';
        setOptionsError(d.hint ? `${base} — ${d.hint}` : base);
      }
    } finally {
      setDraftingOptions(false);
    }
  };

  /** Archive/unarchive this conversation (optimistic; reverts on failure). */
  const toggleArchived = async () => {
    if (!detail || archBusy) return;
    const prevArchivedAt = detail.contact.archived_at ?? null;
    const nextArchived = !prevArchivedAt;
    setArchBusy(true);
    setArchError(null);
    setDetail((d) =>
      d ? { ...d, contact: { ...d.contact, archived_at: nextArchived ? new Date().toISOString() : null } } : d
    );
    try {
      await api.patch(`/api/crm/contacts/${id}`, { archived: nextArchived });
      notifyStatsChanged();
    } catch (e: any) {
      setDetail((d) => (d ? { ...d, contact: { ...d.contact, archived_at: prevArchivedAt } } : d));
      setArchError(e?.response?.data?.detail || e?.response?.data?.error || e.message || 'Could not update the conversation.');
    } finally {
      setArchBusy(false);
    }
  };

  /**
   * Snooze/un-snooze this lead (optimistic; reverts on failure). Mirrors the
   * per-row action in the inbox, worklist, and conversation lists so a
   * recruiter who has already opened the lead doesn't have to navigate back to
   * a list to defer it. A future timestamp hides them from every working list
   * until it passes; null clears it.
   */
  const snooze = async (untilIso: string | null) => {
    if (!detail || snoozeBusy) return;
    const prev = detail.contact.snoozed_until ?? null;
    setSnoozeBusy(true);
    setSnoozeError(null);
    setDetail((d) => (d ? { ...d, contact: { ...d.contact, snoozed_until: untilIso } } : d));
    try {
      await api.patch(`/api/crm/contacts/${id}`, { snoozed_until: untilIso });
      notifyStatsChanged(); // the lead leaves/rejoins the sidebar working counts
    } catch (e: any) {
      setDetail((d) => (d ? { ...d, contact: { ...d.contact, snoozed_until: prev } } : d));
      setSnoozeError(e?.response?.data?.detail || e?.response?.data?.error || e.message || 'Could not update the snooze.');
    } finally {
      setSnoozeBusy(false);
    }
  };

  // Lead-linked study slugs (the reference drawer's default ordering).
  const leadStudySlugs = useMemo(
    () => [...new Set((detail?.leads || []).map((l) => l.study_slug).filter(Boolean))] as string[],
    [detail?.leads]
  );


  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(backTo)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
        >
          <ChevronLeft className="h-4 w-4" /> {backLabel}
        </button>
        <Alert tone="error" icon={false}>{error || 'Contact not found.'}</Alert>
      </div>
    );
  }

  const c = detail.contact;

  // Escalation-banner jump target, narrowed to the two anchor kinds so the
  // thread scrolls (message) or the agent card's screen detail flashes (screen).
  const focusAnchor = escalationFocus?.anchor ?? null;
  const focusNonce = escalationFocus?.nonce ?? 0;
  const focusCommId = focusAnchor && focusAnchor.kind === 'message' ? focusAnchor.commId : null;
  const focusScreenKey = focusAnchor && focusAnchor.kind === 'screen' ? focusAnchor.questionKey : null;

  // A flagged question clicked inside the thread's AI screening summary card
  // rides the SAME jump the escalation banner uses (owner ask 2026-08-05).
  const focusScreenQuestion = (questionKey: string) =>
    setEscalationFocus((prev) => ({ anchor: { kind: 'screen', questionKey }, nonce: (prev?.nonce || 0) + 1 }));

  // Claim / resolve a flag straight from the banner. The agent card owns the
  // escalation list, so its refresh is what makes the row update (or leave).
  const escalationAction = async (escId: string, action: 'claim' | 'resolve') => {
    await api.post(`/api/crm/agent/escalations/${escId}/${action}`);
    agentThread?.refresh();
  };

  // ---- v2-only prop plumbing (Phase 3). The classic JSX keeps its original
  // inline lambdas untouched (toggle-off stays byte-identical); these consts
  // carry the SAME bodies into the v2 header/strip mounts. ----
  const onDraftsChanged = () => { agentThread?.refresh(); setReloadToken((t) => t + 1); };
  // A study-verified stage change may re-point the considering study in the
  // same PATCH, so this mirrors it locally — otherwise the header chip goes
  // stale until the next refetch.
  const onLifecycleChangedV2 = (next: string, studyId?: string) => {
    setDetail((d) => {
      if (!d) return d;
      const contact = { ...d.contact, lifecycle_status: next };
      if (studyId && studyId !== d.contact.primary_study_id) {
        contact.primary_study_id = studyId;
        return { ...d, contact, primary_study: { id: studyId, slug: '', title: '' } };
      }
      return { ...d, contact };
    });
  };
  const onOwnerChangedV2 = (next: string | null) =>
    setDetail((d) => (d ? { ...d, contact: { ...d.contact, owner_user_id: next } } : d));
  const onPrimaryStudyChangedV2 = (next: { id: string; slug: string; title: string } | null) =>
    setDetail((d) => (d ? {
      ...d,
      contact: { ...d.contact, primary_study_id: next?.id ?? null },
      primary_study: next ? { id: next.id, slug: next.slug, title: next.title } : null,
    } : d));

  // Final disposition (not interested / not eligible / screen failed /
  // unreachable): the lead has left the funnel, so the header shows "No study
  // assigned" instead of a considered study. The history stays in the Profile
  // tab + timeline. This is a display rule — primary_study_id is left intact so
  // reactivating the lead restores its study.

  return (
    <div className="space-y-4">
      {/* ONE responsive header, rendered once: Call plus the stage/owner/study
          selects, with everything else folded into the ⋯ Actions menu — which
          IS the bottom sheet below sm. */}
      <LeadHeader
        detail={detail}
        backTo={backTo}
        backLabel={backLabel}
        scheduleReadiness={scheduleReadiness}
        primaryStudyId={primaryStudyId}
        archError={archError}
        snoozeError={snoozeError}
        optionsError={optionsError}
        draftingOptions={draftingOptions}
        onDraftOptions={draftOptions}
        archBusy={archBusy}
        onToggleArchived={toggleArchived}
        snoozeBusy={snoozeBusy}
        onSnooze={snooze}
        onEdit={() => setEditOpen(true)}
        onOpenStudyRef={() => setRefOpen(true)}
        onBack={() => navigate(backTo)}
        onLifecycleChanged={onLifecycleChangedV2}
        onOwnerChanged={onOwnerChangedV2}
        onPrimaryStudyChanged={onPrimaryStudyChangedV2}
      />

      {msg && <Alert tone="info" icon={false}>{msg}</Alert>}

      {/* Clearance for the docked lead-rail sheet peek (D-12). The real phone
          peek is ~110px + safe-area — 44px handle + 8px gap + the ~58px
          status-line row (its Take-over button keeps the 44px touch target
          below sm) — so phones reserve pb-36 (144px, covers a notch's
          safe-area too); sm..lg the status row collapses (~82px peek) and
          pb-24 suffices. */}
      <div className="grid grid-cols-1 gap-5 max-sm:pb-36 sm:max-lg:pb-24 lg:grid-cols-5">
        {/* Conversation — the screen recruiters live in. One fixed-height card:
            thread scrolls inside it, composer docked at the bottom. */}
        <div className="lg:col-span-3">
          {/* 240px ≈ app bar (3rem) + main padding + the contact header card +
              grid gap — keep in sync with the CRMLayout chrome so the thread
              fills the viewport without page scroll on desktop AND laptop.
              Phones (max-sm) size against the compact identity bar so the
              thread + composer own the screen — classic keeps the original
              13.5rem ≈ app bar + page padding + the two-row bar + grid gap;
              v2 reserves 20.5rem = that same chrome PLUS ~7rem for the docked
              lead-rail sheet peek (D-12: 44px handle + 8px gap + ~58px status
              row — the Take-over button keeps its 44px touch target below sm)
              so the composer's send row is never under the peek at rest
              (min-h-[420px] still wins on short phones; the grid's pb-36
              covers the scrolled case there). dvh (where supported) tracks
              the real visible height as mobile browser chrome collapses.
              Tablets (sm..lg) keep the 68dvh card; v2 relies on the grid's
              pb clearance there instead. The branch keeps classic's rendered
              className byte-identical, token order included. */}
          <div className="flex h-[65vh] min-h-[420px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 supports-[height:1dvh]:max-sm:h-[calc(100dvh-20.5rem)] supports-[height:1dvh]:sm:max-lg:h-[68dvh] sm:min-h-[480px] lg:h-[calc(100vh-240px)]">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <MessageSquare className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Conversation
              </h3>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
                </span>
                Live
              </span>
            </div>
            <CommsThread
              contactId={c.id}
              reloadToken={reloadToken}
              usersById={usersById}
              onPing={onPing}
              contactName={contactName(c) === '(no name)' ? undefined : contactName(c)}
              contactPhone={c.phone_e164}
              focusCommId={focusCommId}
              focusNonce={focusNonce}
              onFocusScreen={focusScreenQuestion}
              channelFilter={notesOnly ? 'note' : null}
              onExitFilter={() => setNotesOnly(false)}
            />
            {/* The ONE attention region — flags + drafts + scheduled sends
                behind a one-line summary. pingTick in the reloadToken sum is
                what drops fired sends off the bar live. */}
            <AttentionStrip
              contactId={c.id}
              agentThread={agentThread}
              schedToken={schedToken + pingTick}
              onEscalationAction={escalationAction}
              onEscalationFocus={(anchor) => { setNotesOnly(false); setEscalationFocus((prev) => ({ anchor, nonce: (prev?.nonce || 0) + 1 })); }}
              onOpenConsole={() => navigate('/crm/agent?tab=escalations')}
              onDraftsChanged={onDraftsChanged}
            />
            <Composer
              key={c.id}
              contactId={c.id}
              studyId={detail.primary_study?.id ?? null}
              contactName={contactName(c) === '(no name)' ? undefined : contactName(c)}
              smsOptOut={c.sms_opt_out}
              hasPhone={!!c.phone_e164}
              hasEmail={!!c.email}
              onSent={() => setReloadToken((t) => t + 1)}
              onScheduled={() => setSchedToken((t) => t + 1)}
              prefill={composerDraft}
              onPrefillConsumed={() => setComposerDraft(null)}
              agentActive={agentThread?.status === 'active'}
              agentDraftPending={!!agentThread?.draftPending}
              onToggleNotesFilter={() => setNotesOnly((n) => !n)}
              notesFilterActive={notesOnly}
            />
          </div>
        </div>

        {/* Tabbed work rail: next steps / screening / profile. Grows to its full
            content height and scrolls with the whole page (no inner scroll box)
            so the profile / next-steps / prescreening cards sit on the page
            instead of being crammed into a short window. */}
        <div className="lg:col-span-2">
          {/* The ONE rail — the agent panel plus the lead's next-steps,
              screening and profile tabs, scrolling independently so the
              thread stays pinned.

              Pinned to the thread card's height on desktop AND laptop. Below
              lg the rail renders itself as the docked bottom sheet (D-12,
              position:fixed — it escapes this wrapper, so the wrapper is
              inert there beyond one grid gap). 240px is the SAME h-12-synced
              constant as the thread card above — the height family's 4th
              member (CRMApp shell min-h · Sidebar sticky column · the thread
              card · this wrapper): change all together or not at all. The
              phone thread card's 20.5rem calc reserves the sheet-peek
              clearance — the family's 5th number, documented on the card. */}
          <div className="lg:h-[calc(100vh-240px)]">
            <LeadRail
              detail={detail}
              enrollments={enrollments}
              reloadToken={reloadToken}
              pingTick={pingTick}
              onSent={() => setReloadToken((t) => t + 1)}
              onChanged={() => { setReloadToken((t) => t + 1); notifyStatsChanged(); }}
              isSuperAdmin={isSuperAdmin}
              reenrolling={reenrolling}
              onReEnroll={reEnroll}
              hasRunnableStudy={(detail.leads || []).some((l) => Boolean(l.study_slug))}
              prefillComposer={(text) => setComposerDraft({ text, mode: 'sms' })}
              scheduleReadiness={scheduleReadiness}
              onToggleGate={onToggleGate}
              agentContactId={id!}
              agentStudyId={(detail as any)?.contact?.primary_study_id || detail.primary_study?.id || null}
              onThreadState={setAgentThread}
              focusScreenKey={focusScreenKey}
              focusNonce={focusNonce}
            />
          </div>
        </div>
      </div>

      <StudyReferenceDrawer
        open={refOpen}
        onClose={() => setRefOpen(false)}
        contactStudySlugs={leadStudySlugs}
        initialStudyId={detail.primary_study?.id ?? null}
      />

      {editOpen && (
        <EditContactModal
          contact={c}
          onClose={() => setEditOpen(false)}
          onSaved={(updated: Contact) => {
            setEditOpen(false);
            if (updated) {
              setDetail((d) => (d ? { ...d, contact: { ...d.contact, ...updated } } : d));
            }
          }}
        />
      )}
    </div>
  );
}
